import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader, 
  CardBody,
  Button,
  Grid,
  FlexContainer
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { 
  FaBuilding, 
  FaMoneyBillWave, 
  FaFileInvoiceDollar, 
  FaChartBar,
  FaClipboardList,
  FaUsers
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const DashboardContainer = styled.div`
  .page-title {
    margin-bottom: 2rem;
    color: var(--primary-color);
    font-size: 2rem;
    font-weight: 700;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  transition: transform 0.2s ease;
  padding: 1.25rem;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  .stat-icon {
    font-size: 2rem;
    color: var(--primary-color);
    margin-bottom: 0.75rem;
  }
  
  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 0.5rem;
    word-break: break-word;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.1rem;
    }
  }
  
  .stat-label {
    color: var(--dark-gray);
    font-size: 0.85rem;
    font-weight: 500;
  }
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ActionCard = styled(Card)`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  .action-icon {
    font-size: 2rem;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }
  
  .action-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-color);
    margin-bottom: 0.5rem;
  }
  
  .action-description {
    color: var(--dark-gray);
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const RecentActivity = styled(Card)`
  .activity-item {
    display: flex;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid var(--border-color);
    
    &:last-child {
      border-bottom: none;
    }
    
    .activity-icon {
      margin-right: 1rem;
      color: var(--primary-color);
    }
    
    .activity-content {
      flex: 1;
      
      .activity-title {
        font-weight: 500;
        color: var(--text-color);
        margin-bottom: 0.25rem;
      }
      
      .activity-time {
        font-size: 0.85rem;
        color: var(--dark-gray);
      }
    }
  }
`;

const StoreKeeperDashboard: React.FC = () => {
  const { user } = useAuth();

  // Mock data for dashboard stats
  const dashboardStats = {
    totalBudget: 150000000,
    totalExpenses: 85000000,
    totalIncome: 120000000,
    pendingApprovals: 5,
    activeLoans: 3,
    totalSuppliers: 12
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <DashboardContainer>
      <h1 className="page-title">Store Keeper Dashboard</h1>
      
      <StatsGrid>
        <StatCard>
          <CardBody>
            <IconWrapper icon={FaBuilding} className="stat-icon" />
            <div className="stat-value">{formatAmount(dashboardStats.totalBudget)}</div>
            <div className="stat-label">Total Budget</div>
          </CardBody>
        </StatCard>
        
        <StatCard>
          <CardBody>
            <IconWrapper icon={FaMoneyBillWave} className="stat-icon" />
            <div className="stat-value">{formatAmount(dashboardStats.totalExpenses)}</div>
            <div className="stat-label">Total Expenses</div>
          </CardBody>
        </StatCard>
        
        <StatCard>
          <CardBody>
            <IconWrapper icon={FaFileInvoiceDollar} className="stat-icon" />
            <div className="stat-value">{formatAmount(dashboardStats.totalIncome)}</div>
            <div className="stat-label">Total Income</div>
          </CardBody>
        </StatCard>
        
        <StatCard>
          <CardBody>
            <IconWrapper icon={FaClipboardList} className="stat-icon" />
            <div className="stat-value">{dashboardStats.pendingApprovals}</div>
            <div className="stat-label">Pending Approvals</div>
          </CardBody>
        </StatCard>
      </StatsGrid>

      <ActionsGrid>
        <ActionCard as={Link} to="/storekeeper/office">
          <CardBody>
            <IconWrapper icon={FaBuilding} className="action-icon" />
            <div className="action-title">Office Management</div>
            <div className="action-description">
              Manage office finances, budgets, expenses, and revenue tracking
            </div>
          </CardBody>
        </ActionCard>
        
        <ActionCard as={Link} to="/storekeeper/office/expenses">
          <CardBody>
            <IconWrapper icon={FaMoneyBillWave} className="action-icon" />
            <div className="action-title">Expense Tracking</div>
            <div className="action-description">
              Record and manage office expenses with receipt tracking
            </div>
          </CardBody>
        </ActionCard>
        
        <ActionCard as={Link} to="/storekeeper/office/budget">
          <CardBody>
            <IconWrapper icon={FaChartBar} className="action-icon" />
            <div className="action-title">Budget Management</div>
            <div className="action-description">
              Create and manage office budgets across different categories
            </div>
          </CardBody>
        </ActionCard>
        
        <ActionCard as={Link} to="/storekeeper/office/loans">
          <CardBody>
            <IconWrapper icon={FaFileInvoiceDollar} className="action-icon" />
            <div className="action-title">Office Loans</div>
            <div className="action-description">
              Track and manage office loans, payments, and balances
            </div>
          </CardBody>
        </ActionCard>
      </ActionsGrid>

      <RecentActivity>
        <CardHeader>
          <h3>Recent Activity</h3>
        </CardHeader>
        <CardBody>
          <div className="activity-item">
            <IconWrapper icon={FaMoneyBillWave} className="activity-icon" />
            <div className="activity-content">
              <div className="activity-title">New expense recorded</div>
              <div className="activity-time">2 hours ago</div>
            </div>
          </div>
          
          <div className="activity-item">
            <IconWrapper icon={FaBuilding} className="activity-icon" />
            <div className="activity-content">
              <div className="activity-title">Budget updated for Q1</div>
              <div className="activity-time">1 day ago</div>
            </div>
          </div>
          
          <div className="activity-item">
            <IconWrapper icon={FaFileInvoiceDollar} className="activity-icon" />
            <div className="activity-content">
              <div className="activity-title">Loan payment processed</div>
              <div className="activity-time">2 days ago</div>
            </div>
          </div>
          
          <div className="activity-item">
            <IconWrapper icon={FaClipboardList} className="activity-icon" />
            <div className="activity-content">
              <div className="activity-title">Expense approval required</div>
              <div className="activity-time">3 days ago</div>
            </div>
          </div>
        </CardBody>
      </RecentActivity>
    </DashboardContainer>
  );
};

export default StoreKeeperDashboard; 